import { BuildingType } from '../types/village.js';
const NO_COST = { wood: 0, clay: 0, iron: 0, grain: 0 };
/*
 * PRICING BASIS
 *
 * The cost curves below are fitted to Tribal Wars 2's published per-level
 * tables, because that game is the reference for how long a village should
 * take to build and we were running at roughly a third of its price. Ten of
 * these buildings have a counterpart there; the total cost of an upgrade now
 * lands within about 10% of theirs at almost every level, and the whole
 * build-out comes to 95% of theirs.
 *
 * Three things stop it being a copy, deliberately:
 *
 *   - We spend four resources where they spend three. Each building keeps its
 *     own character in how it splits the bill (a timber camp still leans on
 *     timber, a wall still eats clay), and grain appears wherever a building
 *     feeds people rather than just housing them. So the totals track theirs
 *     while no single line does.
 *   - Every base and growth figure is nudged off the exact fit. Nothing here
 *     reproduces one of their numbers by accident.
 *   - The Smithy, Stable and Dockyard have no counterpart at all, since they
 *     train everything from one barracks and have no navy. Those three are
 *     priced to sit where they always sat relative to the Barracks.
 *
 * Build times are fitted the same way and live on the same definitions, one
 * curve per building rather than the single shared curve every building used
 * to run on. Production and storage were fitted separately; see config.ts.
 * Population and grain upkeep are ours alone and were left as they were.
 */
export const BUILDINGS = {
    manor_house: {
        type: BuildingType.MANOR_HOUSE,
        name: 'Manor House',
        description: 'Seat of the lord. Each level speeds every other construction a little, widens the window in which a build can be waved through free, and unlocks new trades.',
        maxLevel: 30,
        /*
         * The one curve that genuinely bends.
         *
         * A Manor House is expensive while it is still unlocking buildings, which
         * it finishes doing at 20, and after that each level only buys a little
         * more build speed and a little more standing. Its reference climbs at
         * roughly 1.29 a level to the mid teens and then almost stops, ending at
         * 215,000 rather than the two million a straight 1.29 would demand. A
         * single growth factor priced the middle of that curve at a fifth of what
         * it should be, which is why the breakpoint exists.
         */
        baseCost: { ...NO_COST, wood: 430, clay: 480, iron: 330, grain: 160 },
        costGrowth: 1.285,
        costBreakpoint: 16,
        costGrowthLate: 1.07,
        buildTimeBase: 42,
        buildTimeGrowth: 1.57,
        buildTimeBreakpoint: 7,
        buildTimeGrowthLate: 1.21,
        requires: {},
        sprite: 'manor_house',
    },
    timber_camp: {
        type: BuildingType.TIMBER_CAMP,
        name: 'Timber Camp',
        description: 'Woodcutters working the treeline. Produces timber.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 45, clay: 32, iron: 19 },
        costGrowth: 1.244,
        buildTimeBase: 15,
        buildTimeGrowth: 1.58,
        buildTimeBreakpoint: 11,
        buildTimeGrowthLate: 1.2,
        requires: {},
        /*
         * All three pits share one curve, as the reference does.
         *
         * This sat at 85 growing at 1.123, a shade under the reference at every
         * level. It is now 100 at 1.1195, which tracks it within a few percent the
         * whole way up: 112 an hour at level 1 against their 120, 2,957 at level
         * 30 against their 3,020. The point of moving was not the gap itself but
         * that the costs moved: a village now pays roughly three times what it used
         * to for the same building, so an income tuned against the old prices would
         * have stretched the game to four times its length rather than matching
         * the pacing it was fitted to.
         */
        produces: { resource: 'wood', base: 100, growth: 1.1195 },
        sprite: 'timber_camp',
    },
    clay_pit: {
        type: BuildingType.CLAY_PIT,
        name: 'Clay Pit',
        description: 'Riverbank diggings and drying racks. Produces clay.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 32, clay: 45, iron: 19 },
        costGrowth: 1.244,
        buildTimeBase: 15,
        buildTimeGrowth: 1.58,
        buildTimeBreakpoint: 11,
        buildTimeGrowthLate: 1.2,
        requires: {},
        produces: { resource: 'clay', base: 100, growth: 1.1195 },
        sprite: 'clay_pit',
    },
    iron_mine: {
        type: BuildingType.IRON_MINE,
        name: 'Iron Mine',
        description: 'Shafts cut into the hillside. The one that arms your men: weapons, armour and warhorses are all paid for in iron, and it is the resource a war runs short of first.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 42, clay: 38, iron: 55 },
        costGrowth: 1.246,
        buildTimeBase: 15,
        buildTimeGrowth: 1.58,
        buildTimeBreakpoint: 11,
        buildTimeGrowthLate: 1.2,
        requires: {},
        /*
         * Level with timber and clay now, where it used to sit at 72% of them.
         *
         * That handicap was right for the prices it was written against: iron was
         * about a quarter of what a village spent, so a mine that kept pace with
         * the other two piled up a surplus nobody had a use for. The new cost
         * tables turned that on its head. Counting a full build-out and a
         * representative army together, the split is now roughly 31% timber, 26%
         * clay and 34% iron: swordsmen, heavy cavalry and the Barracks itself are
         * all iron-hungry, and iron is the resource a village runs short of. A
         * mine held at 72% would now be the bottleneck rather than the surplus.
         *
         * Equal thirds is also what the reference does, which is a fair sign that
         * three pits and three prices is a balance that wants to be flat.
         */
        produces: { resource: 'iron', base: 100, growth: 1.1195 },
        sprite: 'iron_mine',
    },
    farm: {
        type: BuildingType.FARM,
        name: 'Farm',
        description: 'Fields and mills. Produces grain and sets the population ceiling.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 30, clay: 33, iron: 16, grain: 12 },
        costGrowth: 1.308,
        buildTimeBase: 22,
        buildTimeGrowth: 1.81,
        buildTimeBreakpoint: 8,
        buildTimeGrowthLate: 1.18,
        requires: {},
        /*
         * Grows at 1.15, the same rate as the population the farm allows.
         *
         * That is the whole point of the number. Grain is the one resource spent
         * continuously rather than once, so what matters is not its size but its
         * ratio to the mouths the same building opens. At 110 and 1.12 against a
         * population curve of 1.21 the two diverged: grain was slack for twenty
         * levels and then a wall, and a farm at its stated cap could feed a third
         * of it. Sharing a growth factor holds the ratio at about 85% of grain
         * spent on upkeep at every level, so the promise the comment always made
         * is finally true: a farm filled to its own cap can just about feed
         * itself and no more.
         */
        produces: { resource: 'grain', base: 70, growth: 1.15 },
        sprite: 'farm',
    },
    warehouse: {
        type: BuildingType.WAREHOUSE,
        name: 'Warehouse',
        description: 'Roofed stores. Raises the cap on every resource you can hold.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 172, clay: 162, iron: 132 },
        costGrowth: 1.208,
        buildTimeBase: 13,
        buildTimeGrowth: 1.65,
        buildTimeBreakpoint: 12,
        buildTimeGrowthLate: 1.19,
        requires: { manor_house: 2 },
        sprite: 'warehouse',
    },
    barracks: {
        type: BuildingType.BARRACKS,
        name: 'Barracks',
        description: 'Drill yard and bunks. Trains infantry; higher levels train faster.',
        maxLevel: 25,
        /*
         * Iron-heavy, and it bends like the Manor for the same reason: a barracks
         * is dear while it is still unlocking units and cheap once it is only
         * drilling them faster. The bend sits at 15 rather than the Manor's 16
         * because that is where the last of its gates falls.
         *
         * This is the least exact of the fitted curves, out by up to 27% in the
         * teens. Its reference makes a step change every time a level unlocks a
         * unit, and our unlocks are not on their schedule, so chasing the steps
         * would import their roster's shape along with its prices.
         */
        baseCost: { ...NO_COST, wood: 290, clay: 340, iron: 400 },
        costGrowth: 1.295,
        costBreakpoint: 15,
        costGrowthLate: 1.09,
        buildTimeBase: 96,
        buildTimeGrowth: 1.25,
        buildTimeBreakpoint: 11,
        buildTimeGrowthLate: 1.31,
        requires: { manor_house: 2 },
        sprite: 'barracks',
    },
    smithy: {
        type: BuildingType.SMITHY,
        name: 'Smithy',
        description: 'Forge and workshop. Improves every unit you field, boosts iron output, and builds siege engines.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 230, clay: 190, iron: 350, grain: 130 },
        costGrowth: 1.255,
        buildTimeBase: 60,
        buildTimeGrowth: 1.42,
        buildTimeBreakpoint: 10,
        buildTimeGrowthLate: 1.22,
        requires: { manor_house: 5, barracks: 1 },
        sprite: 'smithy',
    },
    stable: {
        type: BuildingType.STABLE,
        name: 'Stable',
        description: 'Paddocks and tack rooms. Trains scouts and cavalry.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 280, clay: 230, iron: 350, grain: 140 },
        costGrowth: 1.25,
        buildTimeBase: 70,
        buildTimeGrowth: 1.42,
        buildTimeBreakpoint: 10,
        buildTimeGrowthLate: 1.23,
        requires: { manor_house: 10, barracks: 5, smithy: 5 },
        sprite: 'stable',
    },
    market: {
        type: BuildingType.MARKET,
        name: 'Market',
        description: 'Stalls and a weighbridge. Sends merchants, and each level puts another on the road.',
        maxLevel: 25,
        baseCost: { ...NO_COST, wood: 82, clay: 80, iron: 79 },
        costGrowth: 1.263,
        buildTimeBase: 445,
        buildTimeGrowth: 1.46,
        buildTimeBreakpoint: 6,
        buildTimeGrowthLate: 1.17,
        requires: { manor_house: 3, warehouse: 2 },
        sprite: 'market',
    },
    wall: {
        type: BuildingType.WALL,
        name: 'Stone Wall',
        description: 'Curtain wall and gatehouse. +5% defence per level, and rams must break through it.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 34, clay: 76, iron: 23 },
        costGrowth: 1.265,
        buildTimeBase: 184,
        buildTimeGrowth: 1.72,
        buildTimeBreakpoint: 7,
        buildTimeGrowthLate: 1.25,
        requires: { manor_house: 5, barracks: 3 },
        sprite: 'wall',
    },
    church: {
        type: BuildingType.CHURCH,
        name: 'Church',
        description: 'Chapel and infirmary. When the village is attacked, a share of the fallen defenders are carried in wounded and walk again.',
        maxLevel: 20,
        /*
         * Anchored at both ends rather than level for level.
         *
         * The building it answers to runs to 10 levels and doubles in price at
         * every one of them, which over our 20 would end at eighty million. So
         * the first level costs what theirs costs and the twentieth costs what
         * their tenth costs, and the climb between is spread over twice as many
         * steps. A finished infirmary is worth the same; getting there is a
         * longer road with smaller stones on it.
         */
        baseCost: { ...NO_COST, wood: 38, clay: 42, iron: 20, grain: 15 },
        costGrowth: 1.4,
        buildTimeBase: 128,
        buildTimeGrowth: 1.386,
        requires: { manor_house: 8 },
        sprite: 'church',
    },
    dockyard: {
        type: BuildingType.DOCKYARD,
        name: 'Dockyard',
        description: 'Slipway, quay and crane. Builds ships, and can only be raised in a holding that reaches the sea.',
        /*
         * Fifteen, not twenty. The ships gate at levels 1, 5 and 12, so the top
         * five levels bought nothing but a staffing bill: every building level
         * costs a villager, and a coastal holding with everything maxed was left
         * with 18 free population where the rest of the world gets 23.
         */
        maxLevel: 15,
        baseCost: { ...NO_COST, wood: 380, clay: 210, iron: 120, grain: 40 },
        costGrowth: 1.26,
        buildTimeBase: 90,
        buildTimeGrowth: 1.45,
        buildTimeBreakpoint: 8,
        buildTimeGrowthLate: 1.35,
        requires: { manor_house: 5, warehouse: 3 },
        sprite: 'dockyard',
    },
};
/** Ordered for display on the village screen. */
export const BUILDING_DISPLAY_ORDER = [
    BuildingType.MANOR_HOUSE,
    BuildingType.TIMBER_CAMP,
    BuildingType.CLAY_PIT,
    BuildingType.IRON_MINE,
    BuildingType.FARM,
    BuildingType.WAREHOUSE,
    BuildingType.BARRACKS,
    BuildingType.SMITHY,
    BuildingType.STABLE,
    BuildingType.MARKET,
    BuildingType.CHURCH,
    // Coastal holdings only; the engine refuses it inland, and the list route
    // hides it there so a landlocked village is not taunted with a shipyard.
    BuildingType.DOCKYARD,
    BuildingType.WALL,
];
/**
 * Manor House levels that unlock features beyond the building requirement
 * matrix. Cathedral (15) and Castle Keep (20) are named in the GDD but have
 * no cost or effect defined - they are listed here as not-yet-implemented so
 * the UI can show them greyed out rather than silently omitting them.
 */
export const MANOR_UNLOCKS = [
    { level: 2, label: 'Barracks, Warehouse', implemented: true },
    { level: 3, label: 'Market', implemented: true },
    { level: 5, label: 'Smithy, Stone Wall', implemented: true },
    { level: 10, label: 'Stable', implemented: true },
    { level: 15, label: 'Bailiff, Cathedral', implemented: false },
    { level: 20, label: 'Knights, Castle Keep', implemented: false },
];
export function getBuilding(type) {
    const def = BUILDINGS[type];
    if (!def)
        throw new Error(`Unknown building type: ${type}`);
    return def;
}
//# sourceMappingURL=buildings.js.map